/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { pd } from '@airasia-phoenix/react-native/utils/utils';
import { HorizontalContentCardProps } from '@airasia-phoenix/react-native/widgets/HorizontalContentCardView';
import { TRACKING_CONSTANTS } from '../../configs/constants';
import { HotelRecentSearch } from '../../types';
import { format } from 'date-fns';

// Constants
const DEFAULT_IMAGE_URL =
  'https://static.stgairasia.com/move-pages/hotel_holder.png';
const PRODUCTION_IMAGE_URL =
  'https://static.airasia.com/move-pages/hotel_holder.png';
const DEFAULT_GUEST_COUNT = '0|0|0';
const DEFAULT_PRICE = '0';
const DEFAULT_QUANTITY = 1;

/**
 * Transform HotelRecentSearch to HorizontalContentCardProps format
 */
export const transformRecentSearchToContentCard = (
  recentSearch: HotelRecentSearch,
  screenWidth: number,
  environment?: string,
): HorizontalContentCardProps => {
  // Input validation
  if (!recentSearch || !recentSearch.destination) {
    throw new Error('Invalid recent search data: missing destination');
  }

  if (!screenWidth || screenWidth <= 0) {
    throw new Error('Invalid screen width');
  }

  const totalGuests =
    recentSearch.rooms?.reduce(
      (total, room) =>
        total + (room.adult || 0) + (room.child || 0) + (room.infant || 0),
      0,
    ) || 0;

  const checkInDate = recentSearch.date?.checkin
    ? format(new Date(recentSearch.date.checkin), 'EEE, dd MMM')
    : '';
  const checkOutDate = recentSearch.date?.checkout
    ? format(new Date(recentSearch.date.checkout), 'EEE, dd MMM')
    : '';

  const dateRange =
    checkInDate && checkOutDate ? `${checkInDate} - ${checkOutDate}` : '';
  const cardWidth = screenWidth / 1.5;

  return {
    hasOutlineBorder: true,
    width: cardWidth,
    imageHeight: 72,
    imageWidth: 72,
    favouriteIcon: false,
    imageProps: {
      image: environment === 'prd' ? PRODUCTION_IMAGE_URL : DEFAULT_IMAGE_URL,
      cacheProps: {
        priority: 'normal',
        cachePolicy: 'immutable',
      },
      height: 72,
      width: 72,
    },
    contentDetailsPropsValues: {
      width: cardWidth,
      imageWidth: 72,
      primaryText: recentSearch.destination.name,
      secondaryText: dateRange,
      secondaryTextLines: 1,
      tertiaryInfo: {
        iconAndText: {
          icon: {
            icon: pd.icon.main.social.people_outline,
          },
          text: `${recentSearch.rooms?.length || 0} Room${(recentSearch.rooms?.length || 0) !== 1 ? 's' : ''} • ${totalGuests} Guest${totalGuests !== 1 ? 's' : ''}`,
        },
      },
    },
  };
};
export interface RecentSearchItem extends HorizontalContentCardProps {
  location?: string;
  title?: string;
  details?: {
    country?: string;
    checkindate?: string;
    checkoutdate?: string;
    adult?: number;
    child?: number;
    infant?: number;
    rooms?: number;
    price?: string | number;
    productid?: string;
    recommendationtype?: string;
    destinationcategory?: string;
    currency?: string;
    totalpax?: number;
  };
}
// Helper function to create tracking items
const createTrackingItems = (item: RecentSearchItem, index: number) => {
  const adultCount = item.details?.adult ?? 0;
  const childCount = item.details?.child ?? 0;
  const infantCount = item.details?.infant ?? 0;

  return [
    {
      item_id: item.location ?? '',
      item_name: item?.details?.country ?? '',
      item_category: `${item?.details?.checkindate ?? ''} - ${
        item?.details?.checkoutdate ?? ''
      }`,
      item_category2: item.location ?? '',
      item_category3: `${adultCount}|${childCount}|${infantCount}`,
      item_brand: '',
      item_variant: '',
      quantity: item?.details?.rooms ?? DEFAULT_QUANTITY,
      price: item?.details?.price ?? DEFAULT_PRICE,
      index,
      item_list_name: TRACKING_CONSTANTS.HOTEL_LAST_SEARCHED_LIST,
    },
  ];
};

// Helper function to create tracking parameters
const createTrackingParams = (item: RecentSearchItem, items: any[]) => {
  const adultCount = item.details?.adult ?? 0;
  const childCount = item.details?.child ?? 0;
  const infantCount = item.details?.infant ?? 0;

  return {
    [TRACKING_CONSTANTS.CATEGORY]: `${TRACKING_CONSTANTS.HOTEL_DEALS_CATEGORY}|${item.title ?? ''}`,
    [TRACKING_CONSTANTS.LINE_OF_BUSINESS]: TRACKING_CONSTANTS.LINE_OF_BUSINESS,
    screenName: TRACKING_CONSTANTS.SCREEN_NAME,
    [TRACKING_CONSTANTS.RECOMMENDATION_TYPE]: item.details?.recommendationtype,
    [TRACKING_CONSTANTS.HOTEL_ID]: item?.details?.productid,
    [TRACKING_CONSTANTS.HOTEL_NAME]: item.title ?? '',
    [TRACKING_CONSTANTS.DESTINATION]: item.location ?? '',
    [TRACKING_CONSTANTS.DESTINATION_CATEGORY]:
      item?.details?.destinationcategory,
    [TRACKING_CONSTANTS.DESTINATION_COUNTRY]: item?.details?.country,
    [TRACKING_CONSTANTS.CHECK_IN_DATE]: item?.details?.checkindate,
    [TRACKING_CONSTANTS.CHECK_OUT_DATE]: item?.details?.checkoutdate,
    [TRACKING_CONSTANTS.CURRENCY]: item?.details?.currency?.toUpperCase(),
    [TRACKING_CONSTANTS.TOTAL_PAX]: item?.details?.totalpax,
    [TRACKING_CONSTANTS.LENGTH_OF_STAY]: 0,
    [TRACKING_CONSTANTS.ROOMS]: item?.details?.rooms ?? DEFAULT_QUANTITY,
    [TRACKING_CONSTANTS.ADULT]: adultCount,
    [TRACKING_CONSTANTS.CHILD]: childCount,
    [TRACKING_CONSTANTS.INFANT]: infantCount,
    items,
  };
};

export const handleRecentSearchCardPress = (
  item: RecentSearchItem,
  index = 0,
  logEvent: (action: string, params: object) => void,
) => {
  try {
    // Input validation
    if (!logEvent || typeof logEvent !== 'function') {
      console.error('Invalid logEvent function provided');
      return;
    }

    const items = createTrackingItems(item, index);
    const param = [createTrackingParams(item, items)];

    logEvent(TRACKING_CONSTANTS.ACTIONS.SELECT, { param });
  } catch (error) {
    console.error('Error in handleRecentSearchCardPress:', error);
  }
};

export const getRecentSearchContentCards = (
  data: HotelRecentSearch[],
  logEvent: (action: string, params: object) => void,
  screenWidth: number,
  onPressHandler?: (index: number) => void,
  environment?: string,
): HorizontalContentCardProps[] => {
  // Input validation
  if (!Array.isArray(data)) {
    console.warn('Invalid data provided to getRecentSearchContentCards');
    return [];
  }

  if (!logEvent || typeof logEvent !== 'function') {
    console.warn('Invalid logEvent function provided');
    return [];
  }

  if (!screenWidth || screenWidth <= 0) {
    console.warn('Invalid screenWidth provided');
    return [];
  }

  return data
    .map((recentSearch, index) => {
      try {
        const contentCard = transformRecentSearchToContentCard(
          recentSearch,
          screenWidth,
          environment,
        );

        return {
          ...contentCard,
          onPress: () => {
            try {
              // Create a mock RecentSearchItem for analytics
              const analyticsItem: RecentSearchItem = {
                ...contentCard,
                location: recentSearch.destination?.name,
                title: recentSearch.destination?.name,
                details: {
                  country: recentSearch.destination?.country_code,
                  checkindate: recentSearch.date?.checkin,
                  checkoutdate: recentSearch.date?.checkout,
                  adult:
                    recentSearch.rooms?.reduce(
                      (sum, room) => sum + (room.adult || 0),
                      0,
                    ) || 0,
                  child:
                    recentSearch.rooms?.reduce(
                      (sum, room) => sum + (room.child || 0),
                      0,
                    ) || 0,
                  infant:
                    recentSearch.rooms?.reduce(
                      (sum, room) => sum + (room.infant || 0),
                      0,
                    ) || 0,
                  rooms: recentSearch.rooms?.length || 0,
                },
              };

              handleRecentSearchCardPress(analyticsItem, index, logEvent);
              if (onPressHandler && typeof onPressHandler === 'function') {
                onPressHandler(index);
              }
            } catch (error) {
              console.error(`Error handling press for item ${index}:`, error);
            }
          },
        };
      } catch (error) {
        console.error(
          `Error processing recent search at index ${index}:`,
          error,
        );
        return null;
      }
    })
    .filter(Boolean) as HorizontalContentCardProps[];
};

export const buildRecentSearchViewItem = (
  contentDetailsPropsValues: HorizontalContentCardProps['contentDetailsPropsValues'],
  index: number,
) => {
  // Input validation
  if (!contentDetailsPropsValues) {
    console.warn('Invalid contentDetailsPropsValues provided');
    return {
      item_id: '',
      item_name: '',
      item_category: '',
      item_category2: '',
      item_category3: DEFAULT_GUEST_COUNT,
      item_brand: '',
      item_variant: '',
      quantity: '',
      price: '',
      index: index + 1,
      item_list_name: TRACKING_CONSTANTS.HOTEL_LAST_SEARCHED_LIST,
    };
  }

  return {
    item_id: contentDetailsPropsValues?.primaryText ?? '',
    item_name: '',
    item_category: contentDetailsPropsValues?.secondaryText ?? '',
    item_category2: contentDetailsPropsValues?.primaryText ?? '',
    item_category3: DEFAULT_GUEST_COUNT,
    item_brand: '',
    item_variant: '',
    quantity: '',
    price: '', // replace once API provides price
    index: index + 1,
    item_list_name: TRACKING_CONSTANTS.HOTEL_LAST_SEARCHED_LIST,
  };
};
